import {Injectable, signal} from '@angular/core'
import {
  IAbasecAccount,
  IAutogiro,
  IBorgoCollateral,
  IInterestMap,
  IItem,
  IOwner,
  IValuation,
  TCollectionType
} from '../0-application/types'
import {environment} from '../../environments/environment'
import {map, Observable, of, tap} from 'rxjs'
import {IAccount, ICommunity, IInsurance} from '@sparbanken-syd/teradata-user'
import {BaseService} from '@sparbanken-syd/sparbanken-syd-theme'
import {HttpContext} from '@angular/common/http'
import {X_API_KEY} from '@sparbanken-syd/sparbanken-syd-bankid'
import {SECRET} from '../../environments/secret'


@Injectable({
  providedIn: 'root'
})
export class DataService extends BaseService<IItem> {

  public ovLink = signal<string | null>(null)
  /**
   * User from omsättningsverktyget is set if this is openened from
   * omsättningsverktyget
   */
  public ovUser = signal<string | null>(null)

  protected override baseUrl = `${environment.apiUrl}/items` as any

  /**
   * Get housing loans, have a different url so it has
   * its own condition.
   */
  public getLoans(id: string): Observable<IItem> {
    if (this.current()?.collections.loans) {
      return of(this.current()!.collections.loans) as any
    }
    const url = `${environment.apiUrl}/data/${id}/loans`
    return this.http.get<IItem>(url)
  }

  /**
   * Get any collection, a collection is an array of owners with
   * arrays of collections. Returns the currently stored item if alreay
   * loaded.
   */
  public getCollection<T>(collection: TCollectionType) {
    return this.getOrSaved<T[]>(collection)
  }

  public getPdf() {
    const url = `${environment.apiUrl}/data/${this.current()!.id}/pdf`
    return this.http.get<{ url: string }>(url)
  }

  public checkOwner(sub: string): Observable<IOwner> {
    const url = `${environment.apiUrl}/check`
    return this.http.put<IOwner>(url, {sub})
  }

  /**
   * All "accounts", that is private loans and savings accounts.
   */
  public getAccounts(): Observable<IAccount[][]> {
    return this.getOrSaved<IAccount[][]>('accounts')
  }

  /**
   * Insurances from LEO
   */
  public getInsurances(): Observable<IInsurance[][]> {
    return this.getOrSaved<IInsurance[][]>('insurances')
  }

  /**
   * Abasec -  "investment"
   */
  public getInvestments(): Observable<IAbasecAccount[][]> {
    return this.getOrSaved<IAbasecAccount[][]>('investments')
  }

  /**
   * Abasec -  "investment"
   */
  public getAutogiros(): Observable<IAutogiro[]> {
    return this.getOrSaved<IAutogiro[]>('autogiros')
  }

  public getCommunity(subs: string[]) {
    const url = `${environment.apiUrl}/community`
    return this.http.put<ICommunity[]>(url, subs)
  }

  public approve(id: string, approval: string) {
    const url = `${environment.apiUrl}/data/${id}/approve`
    return this.http.put<IItem[]>(url, {approval})
  }

  /**
   *
   */
  public getInterests(): Observable<IInterestMap> {
    const context = new HttpContext().set(X_API_KEY, SECRET)
    const url = `${environment.interestsUrl}/interests/latest/all-interests`
    return this.http.get<IInterestMap>(url, {context})
  }

  public getBorgoCollaterals(sub: string): Observable<IValuation[]> {
    const url = `${environment.borgoUrl}/profit/collaterals`
    return this.http.put<IBorgoCollateral[]>(url, {sub}).pipe(
      map(collateral => {
        return collateral.map(c => {
          const v: IValuation = {
            collateralId: +c.id,
            collateralValue: c.valuation.collateralValue,
            marketValue: c.valuation.marketValue,
            sub,
            amortizationBasisValue: c.amortizationBasisValue,
            amortizationBasisDebt: c.amortizationBasisDebt
          }
          return v
        })
      })
    )
  }

  public loadDeepLink(id: string): Observable<any> {
    this.ovLink.set(id)
    const url = `${environment.apiUrl}/integrate/${id}`
    return this.http.get<any>(url).pipe(
      tap(r => this.ovUser.set(r.sub))
    )
  }

  public saveToOv(data: any) {
    const url = `${environment.apiUrl}/integrate/${this.ovLink()}`
    return this.http.put(url, {id: this.ovLink(), ...data})
      .pipe(
        tap(() => {
          this.ovLink.set(null)
          this.ovUser.set(null)
        })
      )
      .subscribe()
  }

  private getOrSaved<T>(key: TCollectionType): Observable<T> {
    if (this.current()?.collections[key]) {
      return of(this.current()!.collections[key]) as any
    }
    const url = `${environment.apiUrl}/data/${this.current()!.id}/${key}`
    return this.http.get<T>(url)
  }
}
